import { Box } from '@mui/material';
import React from 'react';
import { isMacOs } from '../../helpers/is-mac-os';
import './styles.css';

/** Maps key part names (lowercase) to their display symbols. */
const KEY_SYMBOLS: Record<string, string> = {
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  command: '⌘',
};

/**
 * Given a comma-separated key string (e.g. "ctrl+arrowup,command+arrowup"), returns the segment
 * to display for the current platform: the one containing "command" on macOS, or "ctrl" otherwise.
 * If there is only one segment, returns it as-is.
 */
export function getTriggerForDisplay(keyString: string): string {
  const segments = keyString.split(',').map((s) => s.trim());

  if (segments.length <= 1) {
    return keyString.trim();
  }

  const mac = isMacOs();
  const chosen = segments.find((s) =>
    mac ? s.toLowerCase().includes('command') : s.toLowerCase().includes('ctrl'),
  );

  return chosen ?? segments[0];
}

/**
 * Returns the trigger as plain text (e.g. "SHIFT + I") for use in tooltips where styled kbd is not desired.
 * Uses the same part mapping as HotkeyDisplay (symbols for arrows/command, uppercase for the rest).
 */
export function formatTriggerAsText(trigger: string): string {
  const rawParts = trigger
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  if (rawParts.length === 0) return '';
  return rawParts
    .map((part) => {
      const symbol = KEY_SYMBOLS[part.toLowerCase()];
      return symbol ?? part.toUpperCase();
    })
    .join(' + ');
}

export type HotkeyDisplayVariant = 'rich' | 'text';

export interface HotkeyDisplayProps {
  /** Key string (e.g. "shift+l" or "ctrl+arrowup,command+arrowup"); platform-appropriate segment is chosen internally. */
  readonly hotkey: string;
  /** "rich" renders styled <kbd> parts; "text" renders plain text via formatTriggerAsText. */
  readonly variant?: HotkeyDisplayVariant;
}

/**
 * Renders a single hotkey string. With variant "rich" (default), shows a sequence of <kbd> parts.
 * With variant "text", shows plain text (e.g. for tooltips) using formatTriggerAsText.
 * Accepts raw key strings (including comma-separated platform variants); picks the right segment via getTriggerForDisplay.
 */
export function HotkeyDisplay({ hotkey, variant = 'rich' }: HotkeyDisplayProps) {
  const displayTrigger = getTriggerForDisplay(hotkey);

  if (variant === 'text') {
    const text = formatTriggerAsText(displayTrigger);
    return text ? <Box component="span">{text}</Box> : null;
  }

  const rawParts = displayTrigger
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);

  if (rawParts.length === 0) return null;

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {rawParts.map((part, i) => {
        const symbol = KEY_SYMBOLS[part.toLowerCase()];
        const display = symbol ?? part.toUpperCase();
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <Box component="span" sx={{ color: 'text.secondary' }}>
                +
              </Box>
            )}
            <kbd className="kbd-light">{display}</kbd>
          </React.Fragment>
        );
      })}
    </Box>
  );
}
